import Vue from 'vue';
import { makeTree } from '../../classifier/tree';
import { ClassificationDoesNotExists, FeatureDoesNotExists, ProductDoesNotExists } from '../exceptions';

const FEATURES_COLORS = [
  'purple',
  'cyan',
  'deep-purple',
  'indigo',
  'pink',
  'brown',
  'lime',
  'blue',
  'light-green',
  'teal',
  'amber',
  'light-blue',
  'yellow',
  'blue-grey',
  'red',
  'orange',
  'deep-orange',
  'green'
].map(color => color + ' lighten-4')

/**
 * Add a new feature to the current active features list.
 * @param {Object} state 
 * @param {Object} feature The feature to add.
 */
function addFeature(state, feature) {
  const index = state.features.length
  const color = FEATURES_COLORS[index % FEATURES_COLORS.length]
  feature = { ...feature, color }
  const features = [...state.features, feature].sort((a, b) => a.id > b.id ? 1 : -1)
  state.features = features
}

/**
 * Add a feature to the given product.
 * 
 * If a feature with the same values exists this new one will replace it.
 *
 * @param {Object} state
 * @param {Object} payload
 * @param {Object} feature the feature to add to the product like
 * {
 *   featureId: Number with the id of the feature,
 *   offset: The offset from the start of the text in that section where the feature is located,
 *   dataId: identifier of the product's data (not product's id) where the feature is,
 *   value: The selected string as feature
 * }
 */
function addTaggedFeatureToProduct(state, { product, taggedFeature }) {
  /* Check if the product exists in the products list */
  const productIndex = state.products.findIndex(({ id, sellerId }) => id === product.id && sellerId === product.sellerId)
  if (productIndex === -1) throw new ProductDoesNotExists()
  product = { ...state.products[productIndex] }

  /* Check if the item does already have the tagged feature */
  const taggedFeatureIndex = product.taggedFeatures.findIndex(t => t.dataId === taggedFeature.dataId &&
    t.featureId === taggedFeature.featureId &&
    t.value === taggedFeature.value &&
    t.offset === taggedFeature.offset
  )

  if (taggedFeatureIndex !== -1) {
    product.taggedFeatures[taggedFeatureIndex] = taggedFeature
  } else {
    product.taggedFeatures.push(taggedFeature)
  }

  Vue.set(state.products, productIndex, product)
}

/**
 * Add more products to the products list of the current classification.
 * @param {Object} state
 * @param {Object[]} products
 */
function addProducts(state, products) {
  state.products.push(...products)
}

/**
 * THIS IS NOT A MUTATION.
 * This function is a helper for other mutations to get the index of the given feature.
 * @throws {FeatureDoesNotExists} when the id does not match any feature.
 * @param {Object} state
 * @param {Number} id the id of the feature to get its index
 */
function getFeatureIndex(state, id) {
  const index = state.features.findIndex(f => f.id === id)
  if (index === -1) throw new FeatureDoesNotExists()
  return index
}

/**
 * Remove a feature by name from the features list and from all the products that could
 * have it.
 * @param {Object} state
 * @param {String} id The id of the feature to remove.
 */
function removeFeature(state, id) {
  state.features = state.features.filter(f => f.id !== id)
  state.products.forEach((product, index) => {
    Vue.set(state.products, index, { ...product, taggedFeatures: product.taggedFeatures.filter(f => f.featureId !== id) })
  })
}

/**
 * Remove a tagged feature from a product's taggedFeatures array.
 *
 * @param {Object} state
 * @param {Object} payload
 * @param {Object} product with id and sellerId
 * @param {Object} taggedFeature with id  
 */
function removeTaggedFeatureFromProduct(state, { product, taggedFeature }) {
  const index = state.products.findIndex(({ id, sellerId }) => id === product.id && sellerId === product.sellerId)
  if (index === -1) throw new ProductDoesNotExists()

  product = {
    ...state.products[index],
    taggedFeatures: state.products[index].taggedFeatures.filter(t => t.id !== taggedFeature.id)
  }

  Vue.set(state.products, index, product)
}

/**
 * Set the current active classification.
 * This will reset all the state, only the classifications will be keep.
 * @param {Object} state 
 * @param {Number} id The id of the classification to set as current.
 */
function setClassification(state, id) {
  const classification = state.classifications.find(c => c.id === id)

  if (id && !classification) throw new ClassificationDoesNotExists()


  state.classification = id ? { ...classification } : null;
  state.feature = null
  state.features = []
  state.products = []
  state.productsPage = 0
  state.selection = null
}

/**
 * Set the array of available classifications.
 * @param {Object} state 
 * @param {Object[]} classifications The classifications to set.
 */
function setClassifications(state, classifications) {
  state.classifications = makeTree(classifications)
}

/**
 * Set the features in the store.
 * @param {Object} state 
 * @param {Object[]} features The array of features to set.
 */
function setFeatures(state, features) {
  state.features = features.map((feature, index) => ({
    ...feature,
    color: FEATURES_COLORS[index % FEATURES_COLORS.length],
    onlyMissing: false
  }))
}

/**
 * PLEASE AVOID CALLING THIS MUTATION DIRECTLY, AND USE THE `setOnlyMissing` ACTION INSTEAD.
 * Set the `onlyMissing` property of a feature identified by its id.
 * @param {Object} state
 * @param {Object} payload
 * @param {Number} payload.id the id of the feature to set its property
 * @param {Boolean} payload.value the value to set in the property.
 */
function setOnlyMissing(state, { id, value }) {
  const index = getFeatureIndex(state, id)
  const feature = { ...state.features[index], onlyMissing: value }
  Vue.set(state.features, index, feature)
}

/**
 * Set the loadingAddFeatureToProduct flag to indicate that is creating a feature
 * and adding it to the current selected product.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingAddTaggedFeatureToProduct(state, value) {
  state.loadingAddTaggedFeatureToProduct = value
}

/**
 * Set the loading classifications flag.
 * @param {Object} state 
 * @param {Boolean} value The value to set.
 */
function setLoadingClassifications(state, value) {
  state.loadingClassifications = value
}

/**
 * Set the flag to indicate that a feature is being created.
 * @param {Object} state 
 * @param {Boolean} value The value to set.
 */
function setLoadingCreateFeature(state, value) {
  state.loadingCreateFeature = value
}

/**
 * Set the loading features flag.
 * @param {Object} state 
 * @param {Boolean} value The value to set.
 */
function setLoadingFeatures(state, value) {
  state.loadingFeatures = value
}

/**
 * Set the loading flag of the products.
 * @param {Object} state
 * @param {Boolean} value 
 */
function setLoadingProducts(state, value) {
  state.loadingProducts = value
}

/**
 * Set the loading flag that indicates that there is a tagged feature being removed.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingRemoveTaggedFeature(state, value) {
  state.loadingRemoveTaggedFeature = value
}

/**
 * Set the products in the store.
 * @param {Object} state 
 * @param {Object[]} products The array of products to set.
 */
function setProducts(state, products) {
  state.products = products
}

/**
 * Set the last products' page obtained from the backend.
 * @param {Object} state
 * @param {Number} page
 */
function setProductsPage(state, page) {
  state.productsPage = page
}

/**
 * Set the how many pages the current active classification have.
 * @param {Object} state
 * @param {Number} value
 */
function setProductsPagesLength(state, value) {
  state.productsPagesLength = value
}

/**
 * Add the field 'removing' as true in the feature with the given name.
 * @param {Object} state
 * @param {String} id The id of the feature to set as removing.
 */
function setRemovingFeature(state, id) {
  const index = getFeatureIndex(state, id)
  const feature = { ...state.features[index], removing: true }
  Vue.set(state.features, index, feature)
}

/**
 * Set the value for the search input.
 * @param {Object} state
 * @param {String} value
 */
function setSearchInput(state, value) {
  state.searchInput = value
}

/**
 * Set the current selected feature that was selected with the feature selector
 * that is in the toolbar.
 * @param {Object} state
 * @param {Object} feature
 */
function setSelectedFeature(state, feature) {
  state.selectedFeature = feature
}

/**
 * Set the selection object in the store.
 * @param {Object} state
 * @param {Object} selection
 */
function setSelection(state, selection) {
  state.selection = selection
}

/**
 * Set the visibility of the sidebar.
 * @param {Object} state
 * @param {Boolean} value The value to set.
 */
function setShowSidebar(state, value) {
  state.show.sidebar = value
}

export default {
  addFeature,
  addTaggedFeatureToProduct,
  addProducts,
  removeFeature,
  removeTaggedFeatureFromProduct,
  setClassification,
  setClassifications,
  setFeatures,
  setOnlyMissing,
  setLoadingAddTaggedFeatureToProduct,
  setLoadingClassifications,
  setLoadingCreateFeature,
  setLoadingFeatures,
  setLoadingProducts,
  setLoadingRemoveTaggedFeature,
  setProducts,
  setProductsPage,
  setProductsPagesLength,
  setRemovingFeature,
  setSearchInput,
  setSelectedFeature,
  setSelection,
  setShowSidebar
}